import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, BookOpen, TrendingUp, Award,
  Eye, Edit, Trash2, Search, BarChart2,
} from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_LABELS = { student: 'Étudiant', instructor: 'Instructeur', admin: 'Administrateur' };
const ROLE_COLORS = {
  admin:      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  instructor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  student:    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

const Admin = () => {
  const navigate = useNavigate();

  const [users, setUsers]               = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch]             = useState('');
  const [roleFilter, setRoleFilter]     = useState('all');
  const [changingRole, setChangingRole] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  const [stats, setStats]               = useState(null);
  const [recentCourses, setRecentCourses] = useState([]);

  // ─── Chargement des stats globales ───────────────────────────────────────
  useEffect(() => {
    axios.get('/api/analytics/dashboard')
      .then(({ data }) => { if (data.success) setStats(data.analytics); })
      .catch(() => {});
  }, []);

  // ─── Chargement des cours récents ────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/admin/courses', { params: { limit: 5 } })
      .then(({ data }) => { if (data.success) setRecentCourses(data.courses); })
      .catch(() => {});
  }, []);

  // ─── Chargement des utilisateurs ────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = { limit: 20, page: 1 };
      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      const { data } = await axios.get('/api/admin/users', { params });
      if (data.success) setUsers(data.users || []);
    } catch {
      toast.error('Erreur chargement utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  }, [search, roleFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      const { data } = await axios.patch(`/api/admin/users/${userId}/role`, { role: newRole });
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
        toast.success(`Rôle mis à jour : ${ROLE_LABELS[newRole]}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur changement de rôle');
    } finally {
      setChangingRole(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer définitivement ${userName} ?`)) return;
    setDeletingUser(userId);
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilisateur supprimé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    } finally {
      setDeletingUser(null);
    }
  };

  const statCards = [
    { icon: Users,     value: stats?.totalUsers       ?? '—', label: 'Utilisateurs',   color: 'bg-blue-500' },
    { icon: BookOpen,  value: stats?.publishedCourses  ?? '—', label: 'Cours publiés',  color: 'bg-green-500' },
    { icon: TrendingUp,value: stats?.totalEnrollments  ?? '—', label: 'Inscriptions',   color: 'bg-yellow-500' },
    { icon: Award,     value: stats?.totalCourses      ?? '—', label: 'Cours au total', color: 'bg-purple-500' },
  ];

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-0 sm:px-2 lg:px-4 py-4 sm:py-6 lg:py-8">

        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">Administration</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Gestion complète de la plateforme MOOC</p>
          </div>
          <button
            onClick={() => navigate('/reports')}
            className="flex items-center justify-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 w-full sm:w-auto"
          >
            <BarChart2 className="w-4 h-4" /> Rapports
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map(s => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-5 flex items-center gap-4">
              <div className={`w-11 h-11 ${s.color} rounded-lg flex items-center justify-center shrink-0`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{s.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">

            {/* Tableau utilisateurs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Utilisateurs
                </h2>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="all">Tous les rôles</option>
                    <option value="student">Étudiants</option>
                    <option value="instructor">Instructeurs</option>
                    <option value="admin">Admins</option>
                  </select>
                </div>
              </div>

              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Aucun utilisateur trouvé</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-2 px-2 font-medium text-gray-600 dark:text-gray-400">Utilisateur</th>
                        <th className="text-left py-2 px-2 font-medium text-gray-600 dark:text-gray-400">Rôle</th>
                        <th className="text-left py-2 px-2 font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">Inscription</th>
                        <th className="text-left py-2 px-2 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-3 px-2">
                            <div className="font-medium text-gray-900 dark:text-white">{user.first_name} {user.last_name}</div>
                            {user.email && <div className="text-xs text-gray-500">{user.email}</div>}
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[user.role] || ROLE_COLORS.student}`}>
                              {ROLE_LABELS[user.role] || user.role}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-gray-500 hidden sm:table-cell">
                            {new Date(user.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1">
                              <select
                                value={user.role}
                                onChange={e => handleRoleChange(user.id, e.target.value)}
                                disabled={changingRole === user.id}
                                className="text-xs px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary disabled:opacity-50 dark:bg-gray-700 dark:text-white"
                              >
                                <option value="student">Étudiant</option>
                                <option value="instructor">Instructeur</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                onClick={() => navigate(`/users`)}
                                className="p-1 text-gray-500 hover:text-primary"
                                title="Voir profil"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                                disabled={deletingUser === user.id}
                                className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                onClick={() => navigate('/users')}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Gérer tous les utilisateurs →
              </button>
            </div>

            {/* Cours récents */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Cours récents</h2>
                <button
                  onClick={() => navigate('/courses')}
                  className="text-sm text-primary hover:underline"
                >
                  Voir tous →
                </button>
              </div>
              {recentCourses.length === 0 ? (
                <p className="text-center py-6 text-gray-500">Aucun cours</p>
              ) : (
                <div className="space-y-3">
                  {recentCourses.map(course => {
                    const instructor = course.instructor;
                    const instructorName = instructor
                      ? `${instructor.first_name || ''} ${instructor.last_name || ''}`.trim()
                      : '—';
                    return (
                      <div key={course.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="font-medium text-gray-900 dark:text-white text-sm truncate">{course.title}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                              course.status === 'published'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}>
                              {course.status === 'published' ? 'Publié' : 'Brouillon'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {instructorName} · {course.enrollments_count || 0} inscrit(s)
                            {course.average_rating ? ` · ★ ${Number(course.average_rating).toFixed(1)}` : ''}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/courses/${course.id}`)}
                          className="p-1.5 text-gray-400 hover:text-primary ml-2 shrink-0"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions rapides */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions rapides</h3>
              <div className="space-y-2">
                {[
                  { label: 'Gérer les utilisateurs',  desc: 'Rôles et permissions',     path: '/users' },
                  { label: 'Générer des rapports',     desc: 'Analyses et statistiques', path: '/reports' },
                  { label: 'Voir les cours',           desc: 'Catalogue complet',        path: '/courses' },
                  { label: 'Enseigner',                desc: 'Tableau de bord instructeur', path: '/instructor' },
                ].map(action => (
                  <button
                    key={action.label}
                    onClick={() => navigate(action.path)}
                    className="w-full text-left p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white text-sm">{action.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{action.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Informations système */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations système</h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Version',         value: '2.0.0' },
                  { label: 'Base de données', value: 'Supabase' },
                  { label: 'Serveur',         value: 'Node.js / PM2' },
                  { label: 'Frontend',        value: 'React / Vercel' },
                ].map(info => (
                  <div key={info.label} className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{info.label}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{info.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
